import * as React from "react";
import { useTranslation } from "react-i18next";
import { RiArrowRightSLine } from "@remixicon/react";
import { Button } from "~/components/ui/button";
import { Link } from "~/components/ui/link";
import { SectionTitle } from "~/components/ui/section-title";
import { cn } from "~/lib/utils";
import type { ProductFeaturesContent } from "~/data/products/types";

interface FeaturesSectionProps {
  content: ProductFeaturesContent;
}

function FeatureIcon({ src }: { src: string }) {
  // eslint-disable-next-line local/no-raw-img -- feature icons are always SVGs
  return <img src={src} alt="" className="size-6 shrink-0" />;
}

// Touch already gets native swipe-scroll from overflow-x-auto — this only adds
// click-and-drag for mouse, same pattern as ~/components/carousel/ScrollCarousel.tsx.
function useDragToScroll() {
  const dragActiveRef = React.useRef(false);
  const dragStartXRef = React.useRef(0);
  const dragStartScrollRef = React.useRef(0);
  const hasDraggedRef = React.useRef(false);

  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (event.pointerType === "touch") return;
    dragActiveRef.current = true;
    hasDraggedRef.current = false;
    dragStartXRef.current = event.clientX;
    dragStartScrollRef.current = event.currentTarget.scrollLeft;
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (!dragActiveRef.current) return;
    const dx = event.clientX - dragStartXRef.current;
    if (!hasDraggedRef.current && Math.abs(dx) > 4) {
      hasDraggedRef.current = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      event.currentTarget.style.cursor = "grabbing";
    }
    if (hasDraggedRef.current) event.currentTarget.scrollLeft = dragStartScrollRef.current - dx;
  }

  function handlePointerUp(event: React.PointerEvent<HTMLDivElement>) {
    if (event.pointerType === "touch") return;
    dragActiveRef.current = false;
    event.currentTarget.style.cursor = "";
  }

  function handleClickCapture(event: React.MouseEvent) {
    if (hasDraggedRef.current) {
      event.stopPropagation();
      hasDraggedRef.current = false;
    }
  }

  return { handlePointerDown, handlePointerMove, handlePointerUp, handleClickCapture };
}

// The `fleet:` classes restyle this for the fleet PDP's dark .fleet scope (Figma
// 10480:408037); the consumer PDPs never render inside it, so they are unaffected.
export function FeaturesSection({ content }: FeaturesSectionProps) {
  const { t } = useTranslation("products");
  const [activeIndex, setActiveIndex] = React.useState(0);
  const drag = useDragToScroll();

  return (
    <section className="fleet:bg-background bg-neutral-50 py-10 lg:py-15">
      <div className="wrap flex flex-col items-center gap-6 lg:gap-12">
        <div className="flex flex-col items-center gap-2 text-center lg:gap-3">
          {/* bouncie-400 ("Sky", Figma's blue/sky) is 3.33:1 against this section's
              bg-neutral-50 — passes WCAG's 3:1 minimum at this large bold size. */}
          <p className="eyebrow fleet:text-text-heading-alt">
            {t(content.eyebrow)}
          </p>
          <SectionTitle className="text-text-heading fleet:max-w-[235px] md:fleet:max-w-[800px] max-w-[800px]">
            {t(content.headline)}
          </SectionTitle>
          {content.description && (
            <p className="text-text-body-alt lg:text-md text-sm leading-5 lg:leading-[1.1]">{t(content.description)}</p>
          )}
        </div>

        {/* Mobile/tablet: horizontally scrollable, edge-to-edge (bouncieweb uses a
            react-slick carousel for this same row — not installed here, so this replicates
            the swipe-to-scroll behavior natively instead of adding that dependency).
            Desktop (lg+): wraps, no scrolling, per Figma.
            wrap-bleed (app.css) + self-stretch make this row reach the true viewport edge at
            every breakpoint below lg, canceling whichever inset .wrap is currently using and
            re-applying it as padding so the chips line up with the card grid below — see the
            comment on that utility for why self-stretch is required alongside it. */}
        <div
          className="wrap-bleed fleet:pb-0 flex cursor-grab [scrollbar-width:none] gap-3 self-stretch overflow-x-auto pb-1 select-none [-ms-overflow-style:none] lg:cursor-auto lg:flex-wrap lg:items-start lg:justify-center lg:overflow-visible lg:pb-0 [&::-webkit-scrollbar]:hidden"
          onPointerDown={drag.handlePointerDown}
          onPointerMove={drag.handlePointerMove}
          onPointerUp={drag.handlePointerUp}
          onPointerCancel={drag.handlePointerUp}
          onClickCapture={drag.handleClickCapture}
        >
          {content.categories.map((category, index) => (
            <button
              key={category.label}
              type="button"
              onClick={(event) => {
                setActiveIndex(index);
                event.currentTarget.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
              }}
              className={cn(
                "fleet:py-[9px] shrink-0 cursor-pointer rounded-full border px-5 py-2.5 text-sm font-bold tracking-[0.14px] whitespace-nowrap",
                index === activeIndex
                  ? "bg-bouncie-900 border-bouncie-900 fleet:bg-bouncie-500 fleet:border-bouncie-500 text-white"
                  : "text-text-body-secondary fleet:border-bouncie-200 fleet:bg-background fleet:text-text-heading border-neutral-100 bg-white",
              )}
            >
              {t(category.label)}
            </button>
          ))}
        </div>

        {/* Grid and CTA sit in their own 24px-gap stack — the 48px gap-12 above them
            (header block, filter row) is a different, larger gap per Figma. */}
        <div className="flex w-full flex-col gap-6">
          {/* From sm, every category's grid is stacked in the same grid cell with inactive
              ones invisible, so the section keeps the tallest category's height — switching
              to a category with fewer cards (e.g. Access & Users) doesn't shift the layout
              (BNC-5553). On mobile (below sm) the cards stack in one column, where holding the
              tallest height left ~450-500px of empty space under a 3-card category, so there
              inactive grids leave the layout and the section fits the active category (agreed
              with BNC-5553's author). */}
          <div className="grid w-full">
            {content.categories.map((category, index) => (
              <div
                key={category.label}
                inert={index !== activeIndex}
                className={cn(
                  "fleet:gap-6 col-start-1 row-start-1 grid w-full grid-cols-1 content-start gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3",
                  index !== activeIndex && "invisible max-sm:hidden",
                )}
              >
                {category.cards.map((card) => (
                  <div
                    key={card.title}
                    className="fleet:rounded-md fleet:border-[#273142] fleet:bg-[var(--background-alt)] fleet:px-4 fleet:py-[23px] lg:fleet:p-[31px] flex flex-col gap-4 rounded-2xl border border-neutral-100 bg-white p-8"
                  >
                    {category.icon && <FeatureIcon src={category.icon} />}
                    <div className="flex flex-col gap-2">
                      <p className="font-display text-text-heading text-md leading-[1.15] font-extrabold">{t(card.title)}</p>
                      <p className="text-text-body-alt fleet:text-text-body-secondary lg:fleet:text-text-body-alt text-sm leading-5">{t(card.description)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="flex w-full justify-center sm:justify-end">
            <Button asChild
              variant="link"
              size="sm"
              // variant="link"'s default text-primary (bouncie-500) is only 4.49:1 against
              // this section's bg-neutral-50 — a hair under WCAG's 4.5:1 minimum for
              // normal-sized text. bouncie-600 passes at 5.93:1.
              className="text-bouncie-600 fleet:h-5 fleet:gap-1 fleet:px-0 fleet:py-0 fleet:text-base fleet:leading-5 fleet:tracking-[0.16px] fleet:text-bouncie-200 fleet:underline"
              iconRight={<RiArrowRightSLine className="size-5" />}
            >
              <Link data-sid="pdp-features-cta" to={content.ctaHref ?? "/products/drive/features"}>{t(content.ctaLabel)}</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
